import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLocation } from "react-router-dom";

const MUSIC_URL = process.env.REACT_APP_BACKGROUND_MUSIC || "/wedding-music.mp3";

export default function AmbientMusic() {
  const { role, selfieUrl } = useAuth();
  const { pathname } = useLocation();
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("wedding_muted") === "1");
  const [flashing, setFlashing] = useState(false);
  const flashTimerRef = useRef(null);
  const hasPlayedRef = useRef(false);

  // Onboarding is done when user has a role and is NOT on login or onboarding pages
  const onboardingDone =
    role &&
    pathname !== "/login" &&
    pathname !== "/bella-onboarding";

  // Play once onboarding is complete, and flash the button for 10s
  useEffect(() => {
    if (!onboardingDone || !audioRef.current) return;
    
    // Only play once per session
    if (hasPlayedRef.current) return;
    hasPlayedRef.current = true;
    
    if (!muted) {
      audioRef.current.play().catch(() => {});
      // Flash the mute button for 10 seconds so users can find it
      setFlashing(true);
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashing(false), 10000);
    }
    return () => clearTimeout(flashTimerRef.current);
  }, [onboardingDone, muted]);

  // Pause when onboarding not done
  useEffect(() => {
    if (!onboardingDone && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
  }, [onboardingDone]);

  // Auto-pause when any OTHER audio on the page starts playing, resume when it stops
  useEffect(() => {
    const handleOtherPlay = (e) => {
      if (e.target !== audioRef.current && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
    const handleOtherPause = () => {
      setTimeout(() => {
        const othersPlaying = Array.from(document.querySelectorAll("audio"))
          .some(a => a !== audioRef.current && !a.paused);
        if (!othersPlaying && !muted && onboardingDone && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
      }, 300);
    };
    document.addEventListener("play", handleOtherPlay, true);
    document.addEventListener("pause", handleOtherPause, true);
    return () => {
      document.removeEventListener("play", handleOtherPlay, true);
      document.removeEventListener("pause", handleOtherPause, true);
    };
  }, [muted, onboardingDone]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    localStorage.setItem("wedding_muted", muted ? "1" : "0");
  }, [muted]);

  if (!onboardingDone || !MUSIC_URL) return null;

  return (
    <>
      <audio ref={audioRef} src={MUSIC_URL} loop preload="auto" />

      <style>{`
        @keyframes speakerPulse {
          0%, 100% { transform: scale(1);    opacity: 1; }
          40%       { transform: scale(1.35); opacity: 0.7; }
          60%       { transform: scale(1.35); opacity: 0.7; }
        }
        @keyframes ringPulse {
          0%   { box-shadow: 0 0 0 0   rgba(212,175,55,0.7); }
          70%  { box-shadow: 0 0 0 12px rgba(212,175,55,0);  }
          100% { box-shadow: 0 0 0 0   rgba(212,175,55,0);   }
        }
        .speaker-flashing {
          animation: ringPulse 0.9s ease-out infinite;
        }
        .speaker-flashing .speaker-icon {
          animation: speakerPulse 0.9s ease-in-out infinite;
          color: #D4AF37 !important;
        }
      `}</style>

      <button
        onClick={() => {
          const next = !muted;
          setMuted(next);
          setFlashing(false);
          clearTimeout(flashTimerRef.current);
          if (!next && audioRef.current) audioRef.current.play().catch(() => {});
        }}
        data-testid="ambient-music-toggle"
        title={muted ? "Play music" : "Mute music"}
        className={`fixed bottom-4 left-4 z-40 w-12 h-12 rounded-full bg-white/95 backdrop-blur border border-[#D4AF37]/40 shadow-[0_6px_20px_rgba(212,175,55,0.25)] flex items-center justify-center hover:bg-white transition ${flashing ? "speaker-flashing" : ""}`}
      >
        {muted ? (
          <VolumeX className="speaker-icon w-5 h-5 text-[#4A4A4A]" strokeWidth={1.6} />
        ) : (
          <Volume2 className="speaker-icon w-5 h-5 text-[#B5952F]" strokeWidth={1.8} />
        )}
      </button>
    </>
  );
}
